import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { AdminNavigation } from "../components/navigation/AdminNavigation";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Coins,
  Landmark,
  Loader2,
  ReceiptText,
  ShieldCheck,
  FileDown,
  Search,
} from "lucide-react";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHANNELS = [
  { value: "interac", label: "Interac e-Transfer" },
  { value: "wire", label: "Bank Wire" },
  { value: "e_transfer", label: "e-Transfer (other)" },
  { value: "cheque", label: "Cheque" },
  { value: "crypto", label: "Crypto" },
  { value: "other", label: "Other" },
];

const formatTokens = (cents) =>
  ((Number(cents) || 0) / 100).toFixed(2);

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const AdminExternalPayouts = () => {
  const { user, token } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [summary, setSummary] = useState(null);
  const [year, setYear] = useState(new Date().getUTCFullYear());
  const [form, setForm] = useState({
    user_id: "",
    beneficiary_email: "",
    amount_tokens: "",
    channel: "interac",
    bank_reference: "",
    memo: "",
    paid_at: "",
  });

  const fetchRows = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [listRes, sumRes] = await Promise.all([
        axios.get(`${API}/admin/payouts/external?limit=200`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/admin/payouts/external/tax-summary?year=${year}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setRows(listRes.data.rows || []);
      setSummary(sumRes.data || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load external payouts");
    } finally {
      setLoading(false);
    }
  }, [token, year]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const lookupBeneficiary = async () => {
    const email = form.beneficiary_email.trim().toLowerCase();
    if (!email) {
      toast.error("Enter the beneficiary email first");
      return;
    }
    try {
      const res = await axios.get(`${API}/admin/users?q=${encodeURIComponent(email)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = Array.isArray(res.data) ? res.data : res.data?.rows || [];
      const u = list.find((x) => (x.email || "").toLowerCase() === email) || list[0];
      if (u?.user_id) {
        setForm((prev) => ({ ...prev, user_id: u.user_id, beneficiary_email: u.email || prev.beneficiary_email }));
        toast.success(`Beneficiary found: ${u.name || u.email}`);
      } else {
        toast.error("No user with that email");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Beneficiary lookup failed");
    }
  };

  const record = async () => {
    if (!form.user_id || !form.amount_tokens || !form.bank_reference.trim()) {
      toast.error("User ID, amount, and bank reference are all required");
      return;
    }
    const amount = Math.round(parseFloat(form.amount_tokens) * 100);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error("Amount must be a positive number of Tokens");
      return;
    }
    const pin = window.prompt("Enter the PAYOUT_APPROVAL_PIN to record this external payout");
    if (!pin) return;

    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/admin/payouts/external/record`,
        {
          user_id: form.user_id.trim(),
          amount_cents: amount,
          channel: form.channel,
          bank_reference: form.bank_reference.trim(),
          memo: form.memo.trim() || null,
          paid_at: form.paid_at || null,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Payout-Approval-Pin": pin,
          },
        }
      );
      toast.success(`Recorded · ledger entry ${res.data?.ledger_entry_id?.slice(0, 12)}…`);
      setForm({
        user_id: "",
        beneficiary_email: "",
        amount_tokens: "",
        channel: "interac",
        bank_reference: "",
        memo: "",
        paid_at: "",
      });
      await fetchRows();
    } catch (e) {
      const raw = e.response?.data?.detail;
      const msg = raw && typeof raw === "object" ? raw.message : raw || "Record failed";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const exportCsv = () => {
    const header = [
      "payout_id",
      "paid_at",
      "beneficiary_email",
      "beneficiary_name",
      "amount_tokens",
      "channel",
      "bank_reference",
      "ledger_entry_id",
      "recorded_by",
      "recorded_at",
      "memo",
    ].join(",");
    const csv = [
      header,
      ...rows.map((r) =>
        [
          r.payout_id,
          r.paid_at,
          r.beneficiary_email,
          (r.beneficiary_name || "").replace(/,/g, " "),
          formatTokens(r.amount_cents),
          r.channel,
          (r.bank_reference || "").replace(/,/g, " "),
          r.ledger_entry_id,
          r.recorded_by,
          r.recorded_at,
          (r.memo || "").replace(/,/g, " ").replace(/\n/g, " "),
        ].join(",")
      ),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `stardomfans_external_payouts_${year}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!user?.is_admin) {
    return (
      <div className="max-w-lg mx-auto py-24 px-6 text-center">
        <ShieldCheck className="w-10 h-10 text-[#EC008C] mx-auto mb-3" />
        <h1 className="font-['Unbounded'] text-2xl text-white font-bold mb-2">Admin only</h1>
        <p className="text-[#8A92A0]">The External Payout Ledger is restricted to platform admins.</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-10" data-testid="admin-external-payouts-page">
      <AdminNavigation />
      <div className="flex items-center gap-3 mb-2">
        <Link to="/admin/payouts" className="text-[#8A92A0] hover:text-white">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <Landmark className="w-6 h-6 text-[#EC008C]" />
        <h1 className="font-['Unbounded'] text-3xl font-bold text-white">External Payout Ledger</h1>
      </div>
      <p className="text-[#8A92A0] mb-8 text-sm max-w-3xl">
        When you pay an artist by Interac, wire, cheque, or any other channel outside the
        platform, log it here. The system debits their Token wallet (signed ledger row) and
        reconciles the amount against the Segregated Tax Vault for your quarterly StardomFans
        Ltd. tax filings.
      </p>

      {/* --- Tax-year summary card --- */}
      <section className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8" data-testid="tax-summary-grid">
        <SummaryCard
          label={`Tokens bought · ${summary?.year || year}`}
          value={`${formatTokens(summary?.tokens_bought_cents || 0)}`}
          hint="Top-ups via Stripe · fee-free"
        />
        <SummaryCard
          label="Tax held in vault"
          value={`${formatTokens(summary?.tax_held_cents || 0)}`}
          hint="Segregated · never spendable"
          highlight
        />
        <SummaryCard
          label="External payouts"
          value={`${formatTokens(summary?.external_payouts_sent_cents || 0)}`}
          hint="Interac / wire / cheque"
        />
        <SummaryCard
          label="Internal payouts"
          value={`${formatTokens(summary?.internal_payouts_sent_cents || 0)}`}
          hint="Day-11 AI auto-split"
        />
      </section>

      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Label htmlFor="tax-year" className="text-[#8A92A0]">Tax year:</Label>
          <Input
            id="tax-year"
            type="number"
            className="w-28 bg-[#0A0E18] border-[#1A2030] text-white"
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value, 10) || year)}
            data-testid="tax-year-input"
          />
        </div>
        <Button
          onClick={exportCsv}
          variant="outline"
          className="border-[#1A2030] text-white hover:bg-[#0A0E18]"
          disabled={rows.length === 0}
          data-testid="export-csv-btn"
        >
          <FileDown className="w-4 h-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* --- Record form --- */}
      <section className="card p-6 border border-[#1A2030] rounded-xl bg-[#0A0E18] mb-8" data-testid="record-external-form">
        <div className="flex items-center gap-2 mb-4">
          <ReceiptText className="w-5 h-5 text-[#EC008C]" />
          <h2 className="font-['Unbounded'] text-xl font-bold text-white">Record an external payout</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-[#8A92A0]">Beneficiary email</Label>
            <div className="flex gap-2 mt-1">
              <Input
                placeholder="artist@example.com"
                value={form.beneficiary_email}
                onChange={(e) => setForm({ ...form, beneficiary_email: e.target.value })}
                className="bg-[#050810] border-[#1A2030] text-white"
                data-testid="beneficiary-email-input"
              />
              <Button
                variant="outline"
                onClick={lookupBeneficiary}
                className="border-[#1A2030] text-white hover:bg-[#0A0E18]"
                data-testid="lookup-beneficiary-btn"
              >
                <Search className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div>
            <Label className="text-[#8A92A0]">Beneficiary user_id (auto-fills)</Label>
            <Input
              placeholder="usr_..."
              value={form.user_id}
              onChange={(e) => setForm({ ...form, user_id: e.target.value })}
              className="bg-[#050810] border-[#1A2030] text-white font-mono text-sm mt-1"
              data-testid="beneficiary-userid-input"
            />
          </div>

          <div>
            <Label className="text-[#8A92A0]">Amount (Tokens)</Label>
            <Input
              type="number"
              step="0.01"
              min="0.01"
              placeholder="e.g. 250.00"
              value={form.amount_tokens}
              onChange={(e) => setForm({ ...form, amount_tokens: e.target.value })}
              className="bg-[#050810] border-[#1A2030] text-white mt-1"
              data-testid="amount-tokens-input"
            />
          </div>
          <div>
            <Label className="text-[#8A92A0]">Channel</Label>
            <select
              value={form.channel}
              onChange={(e) => setForm({ ...form, channel: e.target.value })}
              className="w-full bg-[#050810] border border-[#1A2030] rounded-md px-3 py-2 text-white mt-1"
              data-testid="channel-select"
            >
              {CHANNELS.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-2">
            <Label className="text-[#8A92A0]">Bank reference (Interac ref / wire number / cheque #)</Label>
            <Input
              placeholder="INT-2026-08172-XYZ"
              value={form.bank_reference}
              onChange={(e) => setForm({ ...form, bank_reference: e.target.value })}
              className="bg-[#050810] border-[#1A2030] text-white font-mono mt-1"
              data-testid="bank-reference-input"
            />
          </div>

          <div>
            <Label className="text-[#8A92A0]">Paid at (UTC, optional)</Label>
            <Input
              type="datetime-local"
              value={form.paid_at}
              onChange={(e) =>
                setForm({
                  ...form,
                  paid_at: e.target.value ? new Date(e.target.value).toISOString() : "",
                })
              }
              className="bg-[#050810] border-[#1A2030] text-white mt-1"
              data-testid="paid-at-input"
            />
          </div>
          <div>
            <Label className="text-[#8A92A0]">Memo (optional)</Label>
            <Input
              placeholder="Day-11 auto-payout batch #12"
              value={form.memo}
              onChange={(e) => setForm({ ...form, memo: e.target.value })}
              className="bg-[#050810] border-[#1A2030] text-white mt-1"
              data-testid="memo-input"
            />
          </div>
        </div>

        <Button
          onClick={record}
          disabled={saving || !form.user_id || !form.amount_tokens || !form.bank_reference.trim()}
          className="w-full mt-6 bg-[#EC008C] text-black font-semibold h-11 hover:bg-[#FF3399]"
          data-testid="record-external-btn"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Recording…
            </>
          ) : (
            <>
              <Coins className="w-4 h-4 mr-2" />
              Record payout (requires PIN)
            </>
          )}
        </Button>
      </section>

      {/* --- History --- */}
      <section>
        <h2 className="font-['Unbounded'] text-xl font-bold text-white mb-3">
          History <span className="text-[#8A92A0] text-sm font-normal ml-2">({rows.length} entries)</span>
        </h2>
        {loading ? (
          <div className="text-[#8A92A0]">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-[#8A92A0]">No external payouts recorded yet.</div>
        ) : (
          <div className="overflow-x-auto border border-[#1A2030] rounded-xl" data-testid="external-payouts-table">
            <table className="w-full text-sm">
              <thead className="bg-[#0A0E18] text-[#8A92A0]">
                <tr>
                  <th className="text-left px-4 py-3">Paid at</th>
                  <th className="text-left px-4 py-3">Beneficiary</th>
                  <th className="text-right px-4 py-3">Tokens</th>
                  <th className="text-left px-4 py-3">Channel</th>
                  <th className="text-left px-4 py-3">Bank ref</th>
                  <th className="text-left px-4 py-3">Recorded by</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.payout_id}
                    className="border-t border-[#1A2030] text-white"
                    data-testid={`external-payout-row-${r.payout_id}`}
                  >
                    <td className="px-4 py-3 text-[#c0c9d4] whitespace-nowrap">{formatDate(r.paid_at)}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{r.beneficiary_name || "—"}</div>
                      <div className="text-xs text-[#8A92A0]">{r.beneficiary_email}</div>
                    </td>
                    <td className="px-4 py-3 text-right font-mono">{formatTokens(r.amount_cents)}</td>
                    <td className="px-4 py-3 text-[#EC008C] uppercase text-xs">{r.channel}</td>
                    <td className="px-4 py-3 font-mono text-xs text-[#c0c9d4]">{r.bank_reference}</td>
                    <td className="px-4 py-3 text-xs text-[#8A92A0]">{r.recorded_by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

const SummaryCard = ({ label, value, hint, highlight = false }) => (
  <div
    className={`rounded-xl border p-4 ${
      highlight
        ? "border-[#EC008C]/50 bg-[#EC008C]/[0.06]"
        : "border-[#1A2030] bg-[#0A0E18]"
    }`}
  >
    <div className="text-xs uppercase tracking-wide text-[#8A92A0]">{label}</div>
    <div className="text-2xl font-bold text-white font-mono mt-1">{value}</div>
    <div className="text-[10px] text-[#8A92A0] mt-1">{hint}</div>
  </div>
);

export default AdminExternalPayouts;
